var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { getState, setRefresh, setState } from '../../helpers';
import { syncActiveDashboardTheme, runWithDashboardTheme, dashboardPackFromApp } from '../../theme';
import { resolveEffectiveUiTokens } from '../../../components/nodeTheme';
/** Drop hover/zoom listeners before rebuild — prevents stacked handlers on resize/zoom. */
export function clearChartWidgetListeners(group) {
    group.off('mousemove');
    group.off('mouseleave');
    group.off('click');
    group.off('wheel');
}
/** Full rebuild — clears children and runs build (used for live/streaming data). */
export function installChartRebuild(group, app, build) {
    var initialized = false;
    var rebuild = function () {
        clearChartWidgetListeners(group);
        var props = __assign(__assign({}, getState(group)), (initialized ? { _chartRebuild: true } : {}));
        var theme = syncActiveDashboardTheme(resolveEffectiveUiTokens(app, props), app, dashboardPackFromApp(app));
        runWithDashboardTheme(theme, function () {
            var e_1, _a;
            try {
                for (var _b = __values(__spreadArray([], __read(group.children), false)), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    group.remove(child);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
            build(group, app, props);
        });
        initialized = true;
        app.requestRender();
    };
    group.metadata.chartRebuild = rebuild;
    setRefresh(group, function () { return rebuild(); });
    rebuild();
}
/** Rebuild by re-invoking the registered chart factory (works for all chart types). */
export function installRegistryChartRebuild(group, app, factory) {
    var initialized = false;
    var rebuild = function () {
        clearChartWidgetListeners(group);
        var props = __assign(__assign(__assign({}, getState(group)), { x: group.x, y: group.y }), (initialized ? { _chartRebuild: true } : {}));
        var theme = syncActiveDashboardTheme(resolveEffectiveUiTokens(app, props), app, dashboardPackFromApp(app));
        runWithDashboardTheme(theme, function () {
            var e_2, _a, e_3, _b;
            try {
                for (var _c = __values(__spreadArray([], __read(group.children), false)), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var child = _d.value;
                    group.remove(child);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_2) throw e_2.error; }
            }
            var fresh = factory(props, app);
            try {
                for (var _e = __values(__spreadArray([], __read(fresh.children), false)), _f = _e.next(); !_f.done; _f = _e.next()) {
                    var child = _f.value;
                    fresh.remove(child);
                    group.add(child);
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                }
                finally { if (e_3) throw e_3.error; }
            }
            group.metadata._parts = fresh.metadata._parts;
            group.metadata.widgetState = fresh.metadata.widgetState;
            // Prefer the factory's value refresh (gauges/meters) over a full rebuild on setLiveValue.
            if (typeof fresh.metadata.refresh === 'function') {
                group.metadata.refresh = fresh.metadata.refresh;
            }
        });
        initialized = true;
        app.requestRender();
    };
    group.metadata.chartRebuild = rebuild;
    // Keep incremental refresh when the factory already registered one (needle/fill updates).
    // Charts that need rebuild-on-refresh use installChartRebuild instead.
    if (typeof group.metadata.refresh !== 'function') {
        setRefresh(group, function () { return rebuild(); });
    }
}
/** Patch widget state and rebuild chart layers. */
export function updateChartProps(group, patch) {
    var _a;
    setState(group, patch);
    var rebuild = (_a = group.metadata) === null || _a === void 0 ? void 0 : _a.chartRebuild;
    rebuild === null || rebuild === void 0 ? void 0 : rebuild();
}
/** Append a point to a numeric series and rebuild (streaming line/bar charts). */
export function pushChartValue(group, value, maxPoints) {
    if (maxPoints === void 0) { maxPoints = 64; }
    var state = getState(group);
    var data = Array.isArray(state.data) ? __spreadArray([], __read(state.data), false) : [];
    data.push(value);
    while (data.length > maxPoints)
        data.shift();
    var patch = { data: data };
    if (Array.isArray(state.series) && state.series.length) {
        patch.series = state.series.map(function (s, i) { var _a; return i === 0 ? __assign(__assign({}, s), { data: __spreadArray([], __read(data), false) }) : __assign(__assign({}, s), { data: __spreadArray([], __read(((_a = s.data) !== null && _a !== void 0 ? _a : [])), false) }); });
    }
    updateChartProps(group, patch);
}
/** Replace OHLC bar array tail (streaming financial charts). */
export function pushChartOhlc(group, bar, maxBars) {
    if (maxBars === void 0) { maxBars = 64; }
    var state = getState(group);
    var data = Array.isArray(state.data) ? __spreadArray([], __read(state.data), false) : [];
    data.push(bar);
    while (data.length > maxBars)
        data.shift();
    updateChartProps(group, { data: data });
}
